// src/Pages/TrainingProvider/TrainingProviderSchema.js

import * as Yup from "yup";

const MB = 1024 * 1024;

// ----- File size limits (in MB) -----
const LOGO_IMG_FILE_SIZE = 2;
const REGISTRATION_DOCS_FILE_SIZE = 3;
const PAN_CARD_FILE_SIZE = 3;
const COURSE_BROCHURE_FILE_SIZE = 5;
const LAB_IMG_FILE_SIZE = 5;
const PREVIOUS_AFFILIATIONS_DOC_FILE_SIZE = 5;
const FACULTY_QUALIFICATION_FILE_SIZE = 5;
const SIGNATURE_IMG_FILE_SIZE = 2;

const FILE_SIZES = {
    LOGO_IMG_FILE_SIZE,
    REGISTRATION_DOCS_FILE_SIZE,
    PAN_CARD_FILE_SIZE,
    COURSE_BROCHURE_FILE_SIZE,
    LAB_IMG_FILE_SIZE,
    PREVIOUS_AFFILIATIONS_DOC_FILE_SIZE,
    FACULTY_QUALIFICATION_FILE_SIZE,
    SIGNATURE_IMG_FILE_SIZE,
};

const FILE_SELECTION_LIMIT = {
    LAB_IMAGES: 5,
};

const _getFileSize = (size = 0) => size * MB;

const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const PDF_TYPES = ["application/pdf"];

/** Helper — check single file type */
const isFileType = (file, types) => !!file && types.includes(file.type);

/** Helper — for fields that may be single File, FileList, or array */
const normalizeFiles = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof FileList !== "undefined" && value instanceof FileList) {
        return Array.from(value);
    }
    return [value]; // single File
};

// ----- Nested schemas -----

const socialLinksSchema = Yup.object().shape({
    instagram: Yup.string().url("Must be a valid URL").nullable().optional(),
    facebook: Yup.string().url("Must be a valid URL").nullable().optional(),
    linkedin: Yup.string().url("Must be a valid URL").nullable().optional(),
    youtube: Yup.string().url("Must be a valid URL").nullable().optional(),
});

const authorizedPersonSchema = Yup.object().shape({
    name: Yup.string()
        .required("Name of Authorized Person is required")
        .min(2, "Minimum 2 characters"),
    designation: Yup.string()
        .required("Designation is required")
        .min(2, "Minimum 2 characters"),
    resedential_address: Yup.string()
        .required("Residential address is required")
        .min(10, "Address must be at least 10 characters"),
    Staff_Details: Yup.string()
        .required("Teaching staff details are required")
        .min(10, "Please provide proper details"),
});

const coursesAndAffiliationsSchema = Yup.object().shape({
    courses: Yup.array()
        .of(
            Yup.string()
                .trim()
                .min(2, "Course must be at least 2 characters")
        )
        .min(1, "Please add at least 1 course")
        .required("Please add courses"),
    branches: Yup.array()
        .of(
            Yup.string()
                .trim()
                .min(2, "Branch must be at least 2 characters")
        )
        .optional(),
    affiliation_status: Yup.string()
        .oneOf(["Yes", "No"], "Please select affiliation status")
        .required("Affiliation status is required"),
});

const infrastructureFacilitiesSchema = Yup.object().shape({
    buildup_area: Yup.string()
        .required("Total built-up area is required")
        .min(1, "Please enter built-up area"),
    lab_facilities: Yup.string()
        .required("Classroom & Lab facilities are required")
        .min(5, "Please describe the lab facilities"),
    staff_count: Yup.string()
        .required("Teaching/Training staff count is required")
        .matches(/^\d+$/, "Must be a valid number"),
    tools_used: Yup.string()
        .required("Technological tools used are required")
        .min(3, "Please describe the tools used"),
});

const declarationSchema = Yup.object().shape({
    signatory_name: Yup.string()
        .required("Signatory name is required")
        .min(2, "Minimum 2 characters"),
    designation: Yup.string()
        .required("Designation is required")
        .min(2, "Minimum 2 characters"),
    // You’re storing date as "DD/MM/YYYY" string
    date: Yup.string().required("Date is required"),
});

/** Helper — check file with document fallback */
const fileValidation = (
    label,
    allowedTypes,
    sizeLimit,
    { multiple = false, minFiles = 1, maxFiles = null } = {}
) => {
    // helper to get id from URL (either path like /my-profile/123 or query ?id=123)
    const getIdFromUrl = () => {
        if (typeof window === "undefined") return null;
        const segments = window.location.pathname.split("/");
        return !isNaN(segments?.[3]) ? segments[3] : null;
    };

    return Yup.mixed()
        .test(`${label}-required`, (() => {
            if (multiple) {
                return minFiles === 1
                    ? `${label} is required`
                    : `${label} requires at least ${minFiles} file${minFiles > 1 ? "s" : ""}`;
            }
            return `${label} is required`;
        })(), function (value) {
            const { documents } = this.parent;

            // normalizeFiles is expected to exist in your codebase
            const files = normalizeFiles(value);

            // 1) if backend provided document → not required
            if ((!files || files.length === 0) && !!documents && documents[label]) return true;

            // 2) if user is on /my-profile/:id (or has an id in URL) → not required
            const idFromUrl = getIdFromUrl();
            const isOnMyProfile = typeof window !== "undefined" && window.location.pathname.includes("/my-profile");
            if (isOnMyProfile || idFromUrl) return true;

            // 3) otherwise enforce required count
            if (multiple) return files.length >= minFiles;
            return files.length > 0;
        })
        .test(`${label}-type`, [...allowedTypes].includes('pdf') ? "Only PDF files are allowed" : `Only images are allowed (jpg, jpeg, png, webp)`, function (value) {
            if (!value) return true; // nothing to validate here (required handled above)
            const files = normalizeFiles(value);
            if (!files.length) return true;

            // isFileType is expected to exist in your codebase
            return files.every((f) => isFileType(f, allowedTypes));
        })
        .test(`${label}-size`, `${label} must be ≤ ${sizeLimit} MB`, function (value) {
            if (!value) return true;
            const files = normalizeFiles(value);
            if (!files.length) return true;

            return files.every((f) => f.size <= _getFileSize(sizeLimit));
        })
        .test(`${label}-count`, (() => {
            if (!multiple) return "";
            if (maxFiles) return `${label} must be ≤ ${maxFiles} file${maxFiles > 1 ? "s" : ""}`;
            return "";
        })(), function (value) {
            if (!multiple || !value) return true;
            const files = normalizeFiles(value);
            if (!files.length) return true;
            if (maxFiles) return files.length <= maxFiles;
            return true;
        });
};

// ----- Main validation schema -----

const validationSchema = Yup.object({
    // Institutional Details
    academy_name: Yup.string()
        .required("Academy/Institute name is required")
        .min(2, "Must be at least 2 characters")
        .max(150, "Must not exceed 150 characters"),

    user_name: Yup.string()
        .transform(value => {
            if (typeof value !== "string") return value;
            // force lowercase and remove invalid chars
            return value.toLowerCase().replace(/[^a-z1-9_]/g, "");
        })
        .required("Username is required")
        .min(3, "Type at least 3 characters")
        .matches(/^[a-z1-9_]+$/, "Only lowercase letters (a–z), digits (1–9) and underscore (_) allowed"),

    institution_type: Yup.string()
        .required("Institution type is required"),

    est_year: Yup.string()
        .required("Year of establishment is required")
        .matches(/^\d{4}$/, "Must be a valid year")
        .test(
            "valid-year",
            "Year must be between 1950 and current year",
            (value) => {
                if (!value) return false;
                const year = parseInt(value, 10);
                const currentYear = new Date().getFullYear();
                return year >= 1950 && year <= currentYear;
            }
        ),

    legal_status: Yup.string()
        .required("Legal status is required"),

    pan_no: Yup.string()
        .required("PAN number is required")
        .min(10, "PAN must be at least 10 characters")
        .max(10, "PAN must not exceed 10 characters"),

    // Contact / Address
    email: Yup.string()
        .required("Institutional email is required")
        .email("Must be a valid email address"),

    phone_no: Yup.string()
        .required("Official contact number is required")
        .matches(/^[0-9]{10}$/, "Contact number must be exactly 10 digits"),

    website_url: Yup.string()
        .url("Must be a valid URL")
        .nullable()
        .optional(),

    full_address: Yup.string()
        .required("Official registered address is required")
        .min(10, "Address must be at least 10 characters")
        .max(500, "Address must not exceed 500 characters"),

    country_id: Yup.string().required("Country is required"),
    state_id: Yup.string().required("State is required"),
    city_id: Yup.string().required("City is required"),

    pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits"),

    // Social links
    social_links: socialLinksSchema,

    // Authorized Person
    authorized_person_deatils: authorizedPersonSchema,

    // Courses & Affiliations
    courses_and_affiliations: coursesAndAffiliationsSchema,

    // Infrastructure & Facilities
    infrastructure_facilities: infrastructureFacilitiesSchema,

    // Declaration
    declaration_agree: Yup.boolean()
        .oneOf([true], "You must agree to the declaration")
        .required("You must agree to the declaration"),

    declaration: declarationSchema,

    // Files
    logo_img: fileValidation("logo_img", IMAGE_TYPES, LOGO_IMG_FILE_SIZE),

    pan_card: fileValidation("pan_card", PDF_TYPES, REGISTRATION_DOCS_FILE_SIZE),

    registration_docs: fileValidation("registration_docs", PDF_TYPES, PAN_CARD_FILE_SIZE),

    course_brochure: fileValidation("course_brochure", PDF_TYPES, COURSE_BROCHURE_FILE_SIZE),

    // image(s)
    lab_img: fileValidation("lab_img", IMAGE_TYPES, LAB_IMG_FILE_SIZE, { multiple: true, minFiles: 1, maxFiles: FILE_SELECTION_LIMIT.LAB_IMAGES }),

    previous_affiliations_doc: fileValidation("previous_affiliations_doc", PDF_TYPES, PREVIOUS_AFFILIATIONS_DOC_FILE_SIZE),

    faculty_qualification: fileValidation("faculty_qualification", PDF_TYPES, FACULTY_QUALIFICATION_FILE_SIZE),

    signature_img: fileValidation("signature_img", IMAGE_TYPES, SIGNATURE_IMG_FILE_SIZE),

});

export { validationSchema, FILE_SIZES, FILE_SELECTION_LIMIT };
